import { useState } from "react";
import { useTranslation } from "react-i18next";
import useAmenityCatalog from "@/hooks/useAmenityCatalog";
import Amenity from "@/model/Amenity";
import AmenityResponse from "@/model/AmenityResponse";
import PropertyFilters from "@/model/PropertyFilters";
import "@/components/PropertyFiltersPanel/PropertyFiltersPanel.scss";

const EMPTY_FILTERS: PropertyFilters = {
  propertyType: "",
  minBedrooms: "",
  minPrice: "",
  maxPrice: "",
  amenities: [],
};

interface PropertyFiltersPanelProps {
  filters: PropertyFilters;
  onChange: (filters: PropertyFilters) => void;
}

function toggleAmenity(list: Amenity[], value: Amenity): Amenity[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

/** Bedrooms, price and amenities. The property type is picked in the category bar instead. */
function PropertyFiltersPanel({
  filters,
  onChange,
}: PropertyFiltersPanelProps) {
  const { t } = useTranslation();
  const { categories } = useAmenityCatalog();
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  // By default only the important amenities (plus any already picked), like Airbnb's filters.
  const quickAmenities = categories
    .flatMap((category) => category.amenities)
    .filter((amenity) => amenity.important || filters.amenities.includes(amenity.code));

  const renderChip = (amenity: AmenityResponse) => {
    const checked = filters.amenities.includes(amenity.code);
    return (
      <label
        key={amenity.code}
        className={`property-filters-panel__chip${
          checked ? " property-filters-panel__chip--active" : ""
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() =>
            onChange({
              ...filters,
              amenities: toggleAmenity(filters.amenities, amenity.code),
            })
          }
        />
        {amenity.label}
      </label>
    );
  };

  return (
    <div className="property-filters-panel">
      <div className="property-filters-panel__row">
        <div className="property-filters-panel__group">
          <label htmlFor="minBedrooms">{t("filters.minBedroomsLabel")}</label>
          <input
            id="minBedrooms"
            type="number"
            min={0}
            placeholder={t("filters.any")}
            value={filters.minBedrooms}
            onChange={(event) =>
              onChange({ ...filters, minBedrooms: event.target.value })
            }
          />
        </div>

        <fieldset className="property-filters-panel__group">
          <legend>{t("filters.priceLabel")}</legend>
          <div className="property-filters-panel__price-range">
            <input
              type="number"
              min={0}
              inputMode="numeric"
              aria-label={t("filters.minPricePlaceholder")}
              placeholder={t("filters.minPricePlaceholder")}
              value={filters.minPrice}
              onChange={(event) =>
                onChange({ ...filters, minPrice: event.target.value })
              }
            />
            <span aria-hidden="true">—</span>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              aria-label={t("filters.maxPricePlaceholder")}
              placeholder={t("filters.maxPricePlaceholder")}
              value={filters.maxPrice}
              onChange={(event) =>
                onChange({ ...filters, maxPrice: event.target.value })
              }
            />
          </div>
        </fieldset>
      </div>

      <fieldset className="property-filters-panel__group">
        <legend>{t("filters.amenitiesLabel")}</legend>
        {showAllAmenities ? (
          categories.map((category) => (
            <div key={category.code} className="property-filters-panel__category">
              <p>{category.label}</p>
              <div className="property-filters-panel__chips">
                {category.amenities.map(renderChip)}
              </div>
            </div>
          ))
        ) : (
          <div className="property-filters-panel__chips">
            {quickAmenities.map(renderChip)}
          </div>
        )}
        {categories.length > 0 && (
          <button
            type="button"
            className="property-filters-panel__more"
            aria-expanded={showAllAmenities}
            onClick={() => setShowAllAmenities((shown) => !shown)}
          >
            {showAllAmenities ? t("filters.showFewerAmenities") : t("filters.showAllAmenities")}
          </button>
        )}
      </fieldset>

      <div className="property-filters-panel__footer">
        <button
          type="button"
          className="button-ghost"
          onClick={() =>
            onChange({ ...EMPTY_FILTERS, propertyType: filters.propertyType })
          }
        >
          {t("filters.resetButton")}
        </button>
      </div>
    </div>
  );
}

export default PropertyFiltersPanel;
export { EMPTY_FILTERS };
export type { PropertyFiltersPanelProps };
